import makeStyles from "@mui/styles/makeStyles";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  // addPatientSchedulerNote,
  appointmentsModalHelperState,
  createAppointmentSlot,
  isAssignSessionModalOpen,
  schedulerFacility,
  selectedDate,
  selectedPatient,
  setModalHelperState,
  swimLanesViewOpen,
} from '../../../../../store/reducers/appointments';
import { useCallback, useEffect, useState } from "react";
import moment from "moment";
import {
  setAgyleHeaderLoading,
  setSnackbar,
} from "../../../../../store/reducers/globals";
import Grid from "@mui/material/Grid";
import WardPanelSearch from "../../../../ward/map/components/panels/wardPanelSearch";
import AgyleTextField from "../../../../../components/ui/fields/agyleTextField";
import { cloneDeep, isEmpty, isFunction } from "lodash";
import AgyleDialog from "../../../../../components/modal/agyleDialog";
import PropTypes from "prop-types";
import { useRefresh } from "../../../context/refreshContext";
import { APPOINTMENT_MODALS } from "../../../utils/constants/appointmentSessionConstants";
import AgyleSwitch from '../../../../../components/ui/switch/agyleSwitch';
import API from '../../../../../app/api';
import SelectedPatientContext from './selectedPatientContext';
import {currentMap} from '../../../../../store/reducers/ward';

export const useStyles = makeStyles((theme) => ({
  contentStyle: {
    borderTop: "1px solid #494B4C",
    minHeight: "50vh",
    // height: 'max-content',
  },
  patientListContainer: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
    padding: 15,
    height: "100%",
  },
  patientBox: {
    display: "flex",
    alignItems: "center",
    padding: "5px 10px",
    gap: 15,
    borderRadius: 8,
    backgroundColor: theme.palette.background.paperDarker,
  },
  ptName: {
    textTransform: "uppercase",
    fontWeight: "bold",
  },
  gender: {
    marginRight: 8,
    borderRadius: 6,
    color: "#222425",
    display: "flex",
    justifyContent: "center",
    padding: 2,
    "&.Male": {
      backgroundColor: "#92bfe2",
    },
    "&.Female": {
      backgroundColor: "#f4bdd1",
    },
    "&.Unspecified": {
      backgroundColor: "#999999",
    },
  },
  age: {
    color: "#ACAFB3",
    fontSize: 12,
    "&.dncpr": {
      color: "#FFFFFF",
    },
  },
  spaceBetween: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  fullRow: {
    display: "flex",
    flexDirection: "column",
  },
  divRow: {
    display: "flex",
    flexWrap: "nowrap",
    flexDirection: "row",
    alignItems: "center",
  },
  gRow: {
    flexGrow: 1,
    maxWidth: 250,

    "&.small": {
      maxWidth: 150,
    },
    "&.medium": {
      maxWidth: 200,
    },
  },
  dncpr: {
    backgroundColor: "#E25B61",
    borderRadius: 6,
    padding: 5,
  },
  actionStyle: {
    paddingRight: 14,
    paddingBottom: 14,
  },
}));

const AssignSessionModal = ({ dischargeContext, onClose, patientObj, onCloseModal, setSchedulerHour}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { onCalendarRefresh, onSlotRefresh } = useRefresh();

  // const isBarsCtxActive = useSelector(barsContextActive);
  const currMap = useSelector(currentMap);
  const isModalOpen = useSelector(isAssignSessionModalOpen);
  const modalHelperState = useSelector(appointmentsModalHelperState); // here it should has session itself and hour selected -> { session, hour }
  const currentlySelectedDate = useSelector(selectedDate);
  const selectedPtSlice = useSelector(selectedPatient);
  const isSwimLanesViewOpen = useSelector(swimLanesViewOpen);
  const facility = useSelector(schedulerFacility);

  // Main form state
  const [reason, setReason] = useState("");
  const [selectedPt, setSelectedPt] = useState(null);
  const [linkToActivity, setLinkToActivity] = useState(true);

  const selectPt = (pt) => {
    const objToAssign = pt || null;

    const modalStateClone = cloneDeep(modalHelperState);

    setSelectedPt(objToAssign);
    dispatch(
      setModalHelperState({
        ...modalStateClone,
        selectedPatient: objToAssign,
      }),
    );
  };

  useEffect(() => {
    if (!!modalHelperState?.selectedPatient && !selectedPt) {
      setSelectedPt(modalHelperState?.selectedPatient);
    }
  }, [modalHelperState?.selectedPatient, selectedPt]);

  useEffect(() => {
    if (!!patientObj && !selectedPt) {
      setSelectedPt(patientObj);
    }
  }, [patientObj, selectedPt]);

  useEffect(() => {
    if (dischargeContext && selectedPtSlice) {
      setSelectedPt(selectedPtSlice);
    }
  }, [dischargeContext, selectedPtSlice]);

  const getModalTitle = (changedCtx) => {
    if (changedCtx) {
      return "Confirm Review Reservation";
    } else {
      return "Choose Patient";
    }
  };

  const closeModal = (snackbarObj = null) => {
    onClose(APPOINTMENT_MODALS.ASSIGN_SESSION);

    if (snackbarObj) {
      return dispatch(setSnackbar(snackbarObj));
    }
  };

  const handleSubmitChange = useCallback(async () => {
    if (!selectedPt || !modalHelperState?.hour || !reason) {
      return dispatch(
        setSnackbar({
          type: "error",
          message: "Please provide valid patient, appointment hour and reason.",
        }),
      );
    }

    if (isEmpty(selectedPt) || !reason) {
      return dispatch(
        setSnackbar({
          message: "Could not save review status. Please try again later.",
          type: "error",
        }),
      );
    }

    const clonePatient = cloneDeep(selectedPt);
    const patientId = clonePatient?.patient?.__id ? clonePatient?.patient?.__id : clonePatient?.__id;

    const assignedDate = moment(
      `${currentlySelectedDate} ${modalHelperState?.hour}`,
      "DD/MM/YYYY h:mm a",
    ).toDate();
    const slotEndDate = moment(modalHelperState?.hour)
      .add(modalHelperState?.session.slotDurationInMinutes, "minute")
      .toDate();

    if (dischargeContext) {
      const passToDischargeFormObj = {
        sessionId: modalHelperState?.session?.sessionId,
        reviewReason: reason,
        reviewDate: assignedDate,
      };

      onClose(APPOINTMENT_MODALS.ASSIGN_SESSION, passToDischargeFormObj);
      setReason("");
      setSelectedPt(null);
    } else {
      const slotData = {
        assignedSession: modalHelperState?.session?.sessionId,
        wardId: modalHelperState?.session?.wardId || "ED",
        hospitalId: facility || modalHelperState?.session?.hospitalId,
        startDate: moment(modalHelperState?.hour).toDate(),
        endDate: slotEndDate,
        description: reason,
        patientId,
      };
      dispatch(setAgyleHeaderLoading(true));

      try {
        const apiResponse = await dispatch(
          createAppointmentSlot(slotData,modalHelperState.remove, async (created) => {
            closeModal({
              message: `Patient has been successfully appointed on ${moment(created.startDate).format("DD/MM/YYYY HH:mm A")}`,
              type: "success",
            });
            // dispatch(addPatientSchedulerNote({
            //   ptId: patientId,
            //   newValues: slotData,
            //   patientsInWardsRef: clonePatient?.__type === 'com.fortrus.unity.api.model.PatientInWard' && clonePatient?.__id ? clonePatient?.__id : null,
            // }))

            if (linkToActivity) {
              const tags = clonePatient?.patient?.tags ?
                  cloneDeep(clonePatient.patient.tags) : clonePatient?.tags ?
                      cloneDeep(clonePatient?.tags) : [];

              if (!tags?.includes('bedas')) {
                tags.push('bedas');
                await API.updatePatientTags(patientId, tags)
                    .catch(e => {
                      console.error(e);
                      dispatch(setSnackbar({type: 'error', message: 'Patient update failed.'}));
                    });
              }
            }

            if (onCloseModal) {
              if (setSchedulerHour) setSchedulerHour(modalHelperState?.hour);
              onCloseModal();
            }
          },
              false,
              clonePatient?.__type === 'com.fortrus.unity.api.model.PatientInWard' && clonePatient?.__id ? clonePatient?.__id : null
          ),
        );

        if (apiResponse) {
          if (isSwimLanesViewOpen && isFunction(onSlotRefresh)) {
            onSlotRefresh();
          } else if (isFunction(onCalendarRefresh)) {
            onCalendarRefresh();
          }
        }
      } catch (err) {
      } finally {
        dispatch(setAgyleHeaderLoading(false));
      }
    }
  }, [
    selectedPt,
    reason,
    modalHelperState,
    onCalendarRefresh,
    onSlotRefresh,
    isSwimLanesViewOpen,
    currentlySelectedDate,
    linkToActivity,
    modalHelperState?.hour,
  ]);

  const handleCloseModal = useCallback((_event, reason) => {
    if (reason !== "backdropClick") {
      closeModal();
    }
  }, []);

  return (
    isModalOpen && (
      <AgyleDialog
        fullWidth
        noPadding
        width="md"
        dividers={false}
        open={isModalOpen}
        closeButtonOnHeader
        disableEscapeKeyDown
        darkContentBackground
        disableActionsCloseButton
        onClose={handleCloseModal}
        onSave={handleSubmitChange}
        actionStyle={classes.actionStyle}
        submitText={t("Book Appointment")}
        contentStyle={classes.contentStyle}
        disableSubmit={!selectedPt || !reason}
        title={t(getModalTitle(dischargeContext))}
      >
        <Grid item xs={12} className={classes.patientListContainer}>
          {!selectedPt ? (
            <Grid item xs={12} sx={{ my: 1, ml: -1, mr: 1 }}>
              <WardPanelSearch
                autoWidth
                onlyGlobal
                appointments
                maxResults={5}
              />
            </Grid>
          ) : (
            <>
              <SelectedPatientContext
                onRefuse={selectPt}
                patient={selectedPt}
                selectedHr={modalHelperState?.hour}
              />

              <Grid item xs={12} style={{marginLeft: 10}}>
                <AgyleSwitch
                  value={linkToActivity}
                  onChange={e => setLinkToActivity(e.target.checked)}
                  label={t('Link to current attendance / spell')}
                />
              </Grid>

              <Grid item xs={12} sx={{ pl: 1 }}>
                <AgyleTextField
                  fullWidth
                  value={reason}
                  noBorder={false}
                  placeholder={t("Provide reason for appointment")}
                  onChange={(ev) => setReason(ev.target.value)}
                />
              </Grid>
            </>
          )}
        </Grid>
      </AgyleDialog>
    )
  );
};

AssignSessionModal.defaultProps = {
  open: false,
};

AssignSessionModal.defaultProps = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
};

export default AssignSessionModal;
